import { GlobalSearchTrigger } from "@/features/global-search/search-content";
// cmp_app_shell top bar — novem shell language (Faz 0.3 port).
//
// 60px oat-canvas bar with a hairline bottom border (mockup Topbar).
// Carries the app title (serif, per the mockup's title slot) + the
// notification bell (2026-07-12, features/notifications/notification-bell.tsx)
// + the AI assistant toggle. The user block + sign-out moved to the LeftRail
// footer per the mockup layout; route-level titles/breadcrumbs arrive with
// the Track A batches (per-route title plumbing is out of golden-sample
// scope).

import { Menu, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { NotificationBell } from "@/features/notifications/notification-bell";
import { useAiAvailability } from "@/features/support-assistant/availability";
import { TourHelpButton } from "@/features/tour";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/store/ui";

export function TopBar(): React.ReactElement {
  const { t } = useTranslation();
  const aiAvailable = useAiAvailability();
  const aiOpen = useUiStore((s) => s.aiAssistantOpen);
  const toggleAi = useUiStore((s) => s.toggleAiAssistant);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const toggleMobileNav = useUiStore((s) => s.toggleMobileNav);

  return (
    <header
      className={cn(
        "flex h-[60px] shrink-0 items-center justify-between gap-4 border-b",
        "border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 md:px-7",
      )}
      role="banner"
    >
      <div className="flex min-w-0 items-baseline gap-2.5">
        {/* Dar ekranda kenar çubuğunun TEK açma yolu. Geniş ekranda çubuk
            zaten görünür, düğme oradaki tek işlevini kaybeder. */}
        <button
          type="button"
          onClick={toggleMobileNav}
          aria-expanded={mobileNavOpen}
          aria-label={t(mobileNavOpen ? "nav.close_menu" : "nav.open_menu")}
          aria-controls="primary-navigation"
          className={cn(
            "-ml-1 mr-0.5 flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-[8px]",
            "text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
            "md:hidden",
          )}
        >
          <Menu size={18} aria-hidden="true" />
        </button>
        <span className="truncate font-serif text-[19px] font-medium text-[var(--fg-primary)]">
          {t("app.title")}
        </span>
        <span className="hidden font-sans text-[13px] text-[var(--fg-muted)] sm:inline">
          {t("app.tagline")}
        </span>
      </div>

      {/* Şapka anahtarı (C1'in RoleSwitcher'ı) bilinçli olarak KALDIRILDI:
          SEM-5131 sahip kararı tek kenar çubuğu — mod değişimi yok, gruplar
          yetkiye göre görünür (LeftRail). Geri eklemeyin. */}

      {/* Dil seçici bilinçli olarak burada DEĞİL: set-once tercih /me/profile
          yüzeyinde yaşar (oradaki yazım preferred_locale'e de işler) — sahibin
          2026-07-30 kararı. Geri eklemeyin. */}
      {/* Tur çapaları sarmalayıcı <span>'lerde: TourHelpButton ve
          NotificationBell kendi köklerine öznitelik geçirmiyor, ve turun
          hedefi için o bileşenlerin API'sini genişletmek onları turun
          varlığından haberdar ederdi. */}
      <div className="flex shrink-0 items-center gap-2">
        <GlobalSearchTrigger />
        <span data-tour="shell-help" className="flex">
          <TourHelpButton />
        </span>
        <span data-tour="shell-notifications" className="flex">
          <NotificationBell />
        </span>

        {aiAvailable && (
          <button
            type="button"
            data-tour="shell-ai"
            onClick={toggleAi}
            aria-pressed={aiOpen}
            aria-label={t("ai.toggle_drawer")}
            className={cn(
              "flex items-center gap-1.5 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-raised)] px-3 py-1.5",
              "font-sans text-xs font-medium text-[var(--fg-secondary)]",
              "hover:bg-[var(--bg-subtle)]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
              aiOpen && "bg-[var(--accent-subtle-bg)] text-[var(--accent)]",
            )}
          >
            <Sparkles size={14} aria-hidden="true" />
            <span className="hidden sm:inline">{t("ai.button_label")}</span>
          </button>
        )}
      </div>
    </header>
  );
}
